import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  useTheme,
  IconButton,
  CircularProgress
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Loop as LoopIcon
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { authApi } from '../../api/authApi.js'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'

const ResetPassword = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, userType } = useSelector((state) => state.auth)
  const isLoggedIn = !!user

  // Initialize active step: for logged-in users we don't use stepper (or effectively skipped)
  const [activeStep, setActiveStep] = useState(location.state?.otpSent ? 1 : 0)
  const { loading, withLoading } = useLoading()
  const [error, setError] = useState('')
  // Use email from state or logged in user
  const [email, setEmail] = useState(location.state?.email || user?.email || '')

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const theme = useTheme()

  // --- SCHEMAS ---

  // Schema for "Forgot Password" flow - Step 0: Email
  const emailSchema = yup.object({
    email: yup
      .string()
      .email('Invalid email address')
      .required('Email is required'),
  })

  // Schema for "Forgot Password" flow - Step 1: OTP & Reset
  const resetSchema = yup.object({
    otp: yup
      .string()
      .length(6, 'OTP must be 6 digits')
      .required('OTP is required'),
    newPassword: yup
      .string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
      .required('Confirm password is required'),
  })

  // Schema for "Change Password" (Authenticated) flow
  const changePasswordSchema = yup.object({
    currentPassword: yup
      .string()
      .required('Current password is required'),
    newPassword: yup
      .string()
      .min(6, 'New password must be at least 6 characters')
      .required('New password is required')
      .notOneOf([yup.ref('currentPassword')], 'New password cannot be the same as current password'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
      .required('Confirm password is required'),
  })

  // --- FORMS ---

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    setValue: setEmailValue
  } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: email }
  })

  useEffect(() => {
    if (email) {
      setEmailValue('email', email)
    }
  }, [email, setEmailValue])

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors },
  } = useForm({
    resolver: yupResolver(resetSchema),
  })

  const {
    register: registerChange,
    handleSubmit: handleChangeSubmit,
    formState: { errors: changeErrors },
    reset: resetChangeForm
  } = useForm({
    resolver: yupResolver(changePasswordSchema),
  })

  // --- HANDLERS ---
  const handleChangePasswordAuthenticated = async (data) => {
    setError('')
    withLoading(async () => {
      try {
        const payload = {
          current_password: data.currentPassword,
          new_password: data.newPassword
        }

        let response
        if (userType === 'superadmin') {
          response = await authApi.superAdminChangePassword(payload)
        } else {
          response = await authApi.changePassword(payload)
        }

        // Check for "Password changed successfully" or success status
        if (response.data.message === "Password changed successfully" || response.data.success) {
          // toast handled by interceptor
          resetChangeForm()
          if (userType === 'superadmin') {
            navigate('/super-admin/dashboard')
          } else {
            navigate('/org/dashboard')
          }
        } else {
          setError(response.data.message || 'Failed to change password')
        }

      } catch (err) {
        setError(err.response?.data?.message || 'Failed to change password')
      }
    })
  }

  // Steps handling
  const steps = ['Enter Email', 'Reset Password', 'Success']

  const renderPasswordInput = (name, label, showState, setShowState, registerFn, errorObj) => (
    <TextField
      margin="normal"
      fullWidth
      label={label}
      type={showState ? 'text' : 'password'}
      {...registerFn(name)}
      error={!!errorObj}
      helperText={errorObj?.message}
      disabled={loading}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LockIcon color="action" fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowState(!showState)}
              edge="end"
              size="small"
            >
              {showState ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{ mb: 2 }}
    />
  )

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark'
          ? 'radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f0f9ff 0%, #cbebff 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Abstract Background Shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark'
            ? 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark'
            ? 'radial-gradient(circle, rgba(56, 189, 248, 0.15) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle, rgba(14, 165, 233, 0.1) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0,
        }}
      />

      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={theme.palette.mode === 'dark' ? 4 : 0}
          className="fade-in"
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            borderRadius: 4,
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 41, 59, 0.7)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
            boxShadow: theme.palette.mode === 'light'
              ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              : 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
            >
              <LoopIcon sx={{ color: '#fff', fontSize: 32 }} />
            </Box>
            <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {isLoggedIn ? 'Change Password' : 'Reset Password'}
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              {isLoggedIn
                ? 'Update your password for security'
                : 'Follow the steps to reset your password'}
            </Typography>
          </Box>

          {/* Only show Stepper if NOT logged in */}
          {!isLoggedIn && (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* ---------------- LOGGED IN: CHANGE PASSWORD FORM ---------------- */}
          {isLoggedIn ? (
            <Box component="form" onSubmit={handleChangeSubmit(handleChangePasswordAuthenticated)}>

              {renderPasswordInput(
                'currentPassword',
                'Current Password',
                showCurrentPassword,
                setShowCurrentPassword,
                registerChange,
                changeErrors.currentPassword
              )}

              {renderPasswordInput(
                'newPassword',
                'New Password',
                showNewPassword,
                setShowNewPassword,
                registerChange,
                changeErrors.newPassword
              )}

              {renderPasswordInput(
                'confirmPassword',
                'Confirm New Password',
                showConfirmPassword,
                setShowConfirmPassword,
                registerChange,
                changeErrors.confirmPassword
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
            </Box>

          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ mb: 2 }}>Please log in to change your password.</Typography>
              <Button variant="contained" onClick={() => navigate('/org/login')} sx={{ mt: 2 }}>
                Go to Login
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Face Recognition System. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default ResetPassword